import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, MenuItem, Button, Card, CardContent, Grid } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Iconify from '../../../components/iconify';
import { LEAVE_STATUS, LEAVE_STATUS_LABELS } from '../constants';
import { getAllCompanies } from '../../../services/leaveService';
import { isSuperAdmin } from '../../../utils/share';

const LeaveFilters = ({ filters, onFilterChange, onResetFilters, showEmployeeFilter = false }) => {
  const isUserSuperAdmin = isSuperAdmin();
  const [statusFilter, setStatusFilter] = useState(filters.status || '');
  const [startDateFilter, setStartDateFilter] = useState(filters.startDate ? dayjs(filters.startDate) : null);
  const [endDateFilter, setEndDateFilter] = useState(filters.endDate ? dayjs(filters.endDate) : null);
  const [employeeFilter, setEmployeeFilter] = useState(filters.employeeName || '');
  const [companyFilter, setCompanyFilter] = useState(filters.companyId || '');
  const [companiesList, setCompaniesList] = useState([]);

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    onFilterChange('status', value || undefined);
  };

  const handleStartDateChange = (date) => {
    setStartDateFilter(date);
    onFilterChange('startDate', date ? date.format('YYYY-MM-DD') : undefined);
  };

  const handleEndDateChange = (date) => {
    setEndDateFilter(date);
    onFilterChange('endDate', date ? date.format('YYYY-MM-DD') : undefined);
  };

  const handleEmployeeChange = (event) => {
    const value = event.target.value;
    setEmployeeFilter(value);
    onFilterChange('employeeName', value || undefined);
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    setCompanyFilter(value);
    // If value is empty string, it means "All Companies" - don't pass companyId
    // If value is set, pass it to filter by that company
    onFilterChange('companyId', value || undefined);
  };

  const handleReset = () => {
    setStatusFilter('');
    setStartDateFilter(null);
    setEndDateFilter(null);
    setEmployeeFilter('');
    setCompanyFilter(''); // Reset to "All Companies" for admin
    // Reset will trigger onResetFilters which clears all filters
    // For admin, no companyId means all companies
    onResetFilters();
  };

  // Fetch companies list for Super Admin only
  useEffect(() => {
    const fetchCompanies = async () => {
      if (isUserSuperAdmin && showEmployeeFilter) {
        try {
          const response = await getAllCompanies();
          const companies = response?.data?.data || [];
          setCompaniesList(companies);

          // Don't set default company for super admin - show all companies by default
        } catch (error) {
          console.error('Error fetching companies:', error);
          setCompaniesList([]);
        }
      }
    };
    fetchCompanies();
  }, [isUserSuperAdmin, showEmployeeFilter]);

  // Sync local state when filters prop changes
  useEffect(() => {
    setStatusFilter(filters.status || '');
    setStartDateFilter(filters.startDate ? dayjs(filters.startDate) : null);
    setEndDateFilter(filters.endDate ? dayjs(filters.endDate) : null);
    setEmployeeFilter(filters.employeeName || '');
    setCompanyFilter(filters.companyId || '');
  }, [filters]);

  const hasActiveFilters = statusFilter || startDateFilter || endDateFilter || employeeFilter || companyFilter;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={showEmployeeFilter ? (isUserSuperAdmin ? 2 : 2) : 3}>
            <TextField select fullWidth label="Status" value={statusFilter} onChange={handleStatusChange} size="small">
              <MenuItem value="">All Status</MenuItem>
              {Object.values(LEAVE_STATUS).map((status) => (
                <MenuItem key={status} value={status}>
                  {LEAVE_STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {isUserSuperAdmin && showEmployeeFilter && (
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Company"
                value={companyFilter}
                onChange={handleCompanyChange}
                size="small"
              >
                <MenuItem value="">All Companies</MenuItem>
                {companiesList.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {showEmployeeFilter && (
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Employee Name or Email"
                value={employeeFilter}
                onChange={handleEmployeeChange}
                size="small"
                placeholder="Search by name or email"
              />
            </Grid>
          )}

          <Grid item xs={12} md={showEmployeeFilter ? 3 : 4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={startDateFilter}
                onChange={handleStartDateChange}
                inputFormat="DD-MMM-YYYY"
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={showEmployeeFilter ? 3 : 4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={endDateFilter}
                onChange={handleEndDateChange}
                inputFormat="DD-MMM-YYYY"
                minDate={startDateFilter}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={showEmployeeFilter ? (isUserSuperAdmin ? 1 : 2) : 1}>
            <Button
              fullWidth
              variant={hasActiveFilters ? 'outlined' : 'text'}
              color="error"
              onClick={handleReset}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              size="small"
              disabled={!hasActiveFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

LeaveFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  showEmployeeFilter: PropTypes.bool,
};

export default LeaveFilters;
